/**
 * Push custom events to Google Tag Manager dataLayer (forwards to GA4 via GTM).
 * Safe no-op if dataLayer / gtag is unavailable.
 */

const pushToDataLayer = (payload) => {
  try {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch (err) {
    console.warn('GA dataLayer push failed:', err);
  }
};

/**
 * Track a custom GA/GTM event.
 * @param {string} eventName - Event name (e.g. 'clicked_on_magazines')
 * @param {object} [params] - Optional event parameters
 */
export const trackGAEvent = (eventName, params = {}) => {
  if (!eventName) return;

  pushToDataLayer({
    event: eventName,
    ...params
  });

  // Also call gtag if present (e.g. GA4 tag loaded via GTM)
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  } catch (err) {
    console.warn('gtag event failed:', err);
  }
};

/** Meta Pixel standard events (forwarded to Pixel via GTM) */
export const PIXEL_EVENTS = {
  INITIATE_CHECKOUT: 'InitiateCheckout',
  PURCHASE: 'Purchase'
};

/**
 * Push a Meta Pixel standard event to dataLayer for GTM to forward to fbq().
 * @param {string} eventName - Meta standard event (e.g. 'InitiateCheckout', 'Purchase')
 * @param {object} [params] - Pixel event parameters (value, currency, content_name, etc.)
 */
export const trackPixelEvent = (eventName, params = {}) => {
  if (!eventName) return;

  pushToDataLayer({
    event: eventName,
    currency: 'INR',
    ...params
  });
};

/**
 * Meta Pixel InitiateCheckout — fired when user clicks Proceed to Payment.
 */
export const trackPixelCheckout = ({
  value,
  planName,
  planId,
  planPeriod
}) => {
  trackPixelEvent(PIXEL_EVENTS.INITIATE_CHECKOUT, {
    value,
    content_name: planName || '',
    content_ids: planId ? [planId] : [],
    content_type: 'product',
    num_items: 1,
    plan_period: planPeriod || ''
  });
};

/**
 * Meta Pixel Purchase — fired on payment success page.
 */
export const trackPixelPurchase = ({
  value,
  planName,
  paymentId,
  subscriptionId
}) => {
  trackPixelEvent(PIXEL_EVENTS.PURCHASE, {
    value,
    content_name: planName || '',
    transaction_id: paymentId || '',
    content_ids: subscriptionId ? [subscriptionId] : (paymentId ? [paymentId] : []),
    content_type: 'product',
    num_items: 1
  });
};

/** Dashboard tile / CTA click events */
export const GA_EVENTS = {
  CLICKED_ON_GIFTING: 'clicked_on_gifting',
  CLICKED_ON_MAGAZINES: 'clicked_on_magazines',
  CLICKED_ON_QUIZZES: 'clicked_on_quizzes',
  CLICKED_ON_LIBRARY: 'clicked_on_library',
  LOGGED_IN: 'logged_in',
  QUIZ_START: 'quiz_start',
  QUIZ_COMPLETED: 'quiz_completed'
};

/**
 * Map dashboard tile ids to GA event names.
 */
export const trackDashboardTileClick = (tileId) => {
  const map = {
    magazines: GA_EVENTS.CLICKED_ON_MAGAZINES,
    quizzes: GA_EVENTS.CLICKED_ON_QUIZZES,
    // Stories tile on user-dashboard maps to library click event
    stories: GA_EVENTS.CLICKED_ON_LIBRARY,
    library: GA_EVENTS.CLICKED_ON_LIBRARY
  };
  const eventName = map[tileId];
  if (eventName) {
    trackGAEvent(eventName, { tile_id: tileId });
  }
};

/**
 * Track successful user login with email.
 * @param {string} email
 */
export const trackUserLoggedIn = (email) => {
  trackGAEvent(GA_EVENTS.LOGGED_IN, {
    user_email: email || '',
    method: 'otp'
  });
};

/**
 * Track quiz start event
 * @param {string} quizName - Name of the quiz
 * @param {string} quizId - ID of the quiz
 */
export const trackQuizStart = (quizName, quizId) => {
  trackGAEvent(GA_EVENTS.QUIZ_START, {
    quiz_name: quizName,
    quiz_id: quizId
  });
};

/**
 * Track quiz completion event
 * @param {string} quizName - Name of the quiz
 * @param {string} quizId - ID of the quiz
 * @param {number} score - User's score
 * @param {number} totalQuestions - Total questions in quiz
 * @param {number} percentage - User's percentage score
 */
export const trackQuizCompleted = (quizName, quizId, score, totalQuestions, percentage) => {
  trackGAEvent(GA_EVENTS.QUIZ_COMPLETED, {
    quiz_name: quizName,
    quiz_id: quizId,
    score: score,
    total_questions: totalQuestions,
    percentage: percentage
  });
};
